import React from "react";
import { Card, CardContent, CardHeader, Grid, ToggleButton, ToggleButtonGroup } from "@mui/material";
import { avgPickupAgeing, formatNumber, numberPrecision, transformChartDataToTable } from "../../../../helpers/UtilityHelper";
import BarChartIcon from '@mui/icons-material/BarChart';
import TableChartIcon from '@mui/icons-material/TableChart';
import { ComposedChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LabelList } from 'recharts';
import ChartTooltip from "../../../charts/ChartTooltip";
import ChartEmpty from "../../../charts/ChartEmpty";
import { AGEING_COLORS } from "../../../charts/colors";
import InfoBox from "../../../../common/InfoBox";
import MaterialTable from "../../../../common/MaterialTable";

const BUCKET_META = [
    { key: '0_Day', label: 'Picked up on same day', color: AGEING_COLORS[0] },
    { key: '1_Day', label: 'Picked up on next day', color: AGEING_COLORS[1] },
    { key: '2_Days', label: 'Picked up on 2nd day', color: AGEING_COLORS[2] },
    { key: '3_Days', label: 'Picked up on 3rd day', color: AGEING_COLORS[3] },
    { key: '4_Days', label: 'Picked up on 4th day', color: AGEING_COLORS[4] },
    { key: '4+_Days', label: 'Picked up after 4 days', color: AGEING_COLORS[5] },
]

export default function OverallAgeing({ data }) {

    const [view, setView] = React.useState("chart")

    const { rows, allColumns } = transformChartDataToTable(data.lineChartData, avgPickupAgeing.map((z) => {
        const key = `Ageing_${z}_percentage`
        return { key, label: z.replace(/_/g, ' ') }
    }))

    const tableColumns = [
        {
            label: 'Name',
            value: 'name',
            align: 'left'
        },
        ...(allColumns.map((z) => ({
            numeric: true,
            align: 'right',
            value: (row) => {
                return row[z] + '%'
            },
            label: z
        })))
    ]

    const chartData = (data.lineChartData || []).map((row) => {
        const total = BUCKET_META.reduce((s, b) => s + (row[`Ageing_${b.key}_count`] || 0), 0)
        return { ...row, _stackTotal: total }
    })

    const breachedPercentage = (data.totalBreachedShipments / data.totalShipmentPickedUp) * 100

    return (
        <Card elevation={1}>
            <CardHeader title="Overall Pickup Ageing" action={(
                <ToggleButtonGroup color="primary" size="small" exclusive onChange={(e, v) => setView(v)} value={view}>
                    <ToggleButton value="chart">
                        <BarChartIcon />
                    </ToggleButton>
                    <ToggleButton value="table">
                        <TableChartIcon />
                    </ToggleButton>
                </ToggleButtonGroup>
            )} />
            <CardContent>
                <Grid container spacing={1}>
                    <Grid item xs={3}>
                        <InfoBox header="Total Shipments Picked up" value={formatNumber(data.totalShipmentPickedUp)} />
                    </Grid>
                    <Grid item xs={3}>
                        <InfoBox header="Breached Shipments" value={`${formatNumber(data.totalBreachedShipments)} (${numberPrecision(breachedPercentage)}%)`} />
                    </Grid>
                    <Grid item xs={3}>
                        <InfoBox header="On Time Pickups" value={`${formatNumber(data.totalShipmentPickedUp - data.totalBreachedShipments)} (${numberPrecision(100 - breachedPercentage)}%)`} />
                    </Grid>
                    <Grid item xs={3}>
                        <InfoBox header="Avg Pickup Days" value={numberPrecision(data.avgPickupDays)} />
                    </Grid>
                </Grid>
                {view === 'chart' && (
                    (!chartData || chartData.length === 0) ? <ChartEmpty height={420} /> : (
                    <ResponsiveContainer width="100%" height={420}>
                        <ComposedChart data={chartData} margin={{ top: 30, right: 20, bottom: 5, left: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#dee2e6" />
                            <XAxis dataKey="name" fontSize="11px" fontWeight="500" />
                            <YAxis fontSize="11px" fontWeight="500" label={{ value: '# shipments', angle: -90, position: 'insideLeft', fontSize: 11, fill: '#666', textAnchor: 'middle' }} />
                            <Tooltip content={<ChartTooltip />} />
                            <Legend verticalAlign="top" align="center" iconSize="10px" iconType="circle" wrapperStyle={{ fontSize: 11, fontWeight: 500, paddingBottom: 10 }} />
                            {BUCKET_META.map((b, i) => {
                                const isLast = i === BUCKET_META.length - 1
                                return (
                                    <Area
                                        key={b.key}
                                        dataKey={`Ageing_${b.key}_count`}
                                        name={b.label}
                                        stackId="ageing"
                                        type="monotone"
                                        stroke={b.color}
                                        fill={b.color}
                                        fillOpacity={0.85}
                                        strokeWidth={1}
                                        animationEasing="ease-in-out"
                                        animationDuration={1000}
                                    >
                                        <LabelList dataKey={`Ageing_${b.key}_count`} position="top" style={{ fontSize: 9, fontWeight: 600, fill: b.color }} formatter={(v) => (v == null || v === 0) ? '' : v} />
                                        {isLast && (
                                            <LabelList dataKey="_stackTotal" position="top" style={{ fontSize: 10, fontWeight: 600, fill: '#374151' }} formatter={(v) => (v == null || v === 0) ? '' : v.toLocaleString('en-IN')} />
                                        )}
                                    </Area>
                                )
                            })}
                        </ComposedChart>
                    </ResponsiveContainer>
                    )
                )}
                {view === 'table' && (
                    <MaterialTable size="small" exportExcel={false} rows={rows} columns={tableColumns} frozenColumnsCount={1} />
                )}
            </CardContent>
        </Card>
    )
}
